import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import OTPScreen from "components/OTPScreen";
import { v4 as uuidv4 } from "uuid";
import ThankYouPopup from "components/ThankYouPopup";
import { baseUrl } from "api/config";

const ContactUs = ({ setIsDropdownOpen }) => {
  const { t } = useTranslation();
  const [otpPopup, setOtpPopup] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [rncode, setRncode] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);

  const initialValues = {
    name: "",
    email: "",
    contactNumber: "",
    message: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters long.")
      .max(50, "Name cannot exceed 50 characters.")
      .required("Please enter your name."),
    email: Yup.string()
      .email("Please enter a valid email address.")
      .required("Email is required."),
    contactNumber: Yup.string()
      .matches(/^[0-9]+$/, "Contact number must contain only numbers.")
      .min(10, "Contact number must be at least 10 digits long.")
      .max(15, "Contact number cannot exceed 15 digits.")
      .required("Contact number is required."),
    message: Yup.string().required("Message cannot be empty."),
  });

  const handleSubmit = async (values, { resetForm }) => {
    const url = `${baseUrl}/api/contact/us/generate-otp`;
    const rncode = uuidv4();

    const params = {
      email: values.email,
      rnCode: rncode,
      projectCode: "tw_XWWdIQLZ",
    };

    try {
      const response = await axios.post(url, {}, { params });

      if (response.status === 200) {
        setFormValues(values);
        setRncode(rncode);
        setOtpPopup(true);
        resetForm();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    setIsDropdownOpen(false);
  };

  return (
    <>
      {!otpPopup && !showThankYou ? (
        <div className="flex flex-col py-5 px-5">
          <h1 className="text-xl 2xl:text-2xl font-medium mb-4">
            {t("contactUs.heading_01")}
            <span className="">
              {" "}
              <Link
                to="mailto:contact@infohubinnovations.com"
                className="text-blue-500"
              >
                admin@i2pulse.com
              </Link>
            </span>{" "}
            <br />
            {t("contactUs.or")}
            <br />
            {t("contactUs.heading_02")}
          </h1>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              getFieldProps,
              handleSubmit,
              handleReset,
              isSubmitting,
            }) => (
              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
              >
                {/* Name Input */}
                <div className="col-span-1 md:col-span-1 lg:col-span-4">
                  <label
                    htmlFor="name"
                    className="block text-[14px] sm:text-[16px] font-medium text-gray-700 mb-1"
                  >
                    {t("contactUs.name")}
                  </label>
                  <input
                    id="name"
                    type="text"
                    {...getFieldProps("name")}
                    className={`w-full p-1 border rounded ${values.name && !values.name.length ? "border-red-500" : ""
                      }`}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-red-500 text-[12px]"
                  />
                </div>

                {/* Email Input */}
                <div className="col-span-1 md:col-span-1 lg:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-[14px] sm:text-[16px] font-medium text-gray-700 mb-1"
                  >
                    {t("contactUs.email")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...getFieldProps("email")}
                    className={`w-full p-1 border rounded ${values.email && !values.email.length
                        ? "border-red-500"
                        : ""
                      }`}
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-500 text-[12px]"
                  />
                </div>

                {/* Contact Number Input */}
                <div className="col-span-1 md:col-span-1 lg:col-span-2">
                  <label
                    htmlFor="contactNumber"
                    className="block text-[14px] sm:text-[16px] font-medium text-gray-700 mb-1"
                  >
                    {t("contactUs.contactNumber")}
                  </label>
                  <input
                    id="contactNumber"
                    type="text"
                    {...getFieldProps("contactNumber")}
                    className={`w-full p-1 border rounded ${values.contactNumber && !values.contactNumber.length
                        ? "border-red-500"
                        : ""
                      }`}
                  />
                  <ErrorMessage
                    name="contactNumber"
                    component="div"
                    className="text-red-500 text-[12px]"
                  />
                </div>

                {/* Message Textarea */}
                <div className="col-span-1 md:col-span-2 lg:col-span-4">
                  <label
                    htmlFor="message"
                    className="block text-[14px] sm:text-[16px] font-medium text-gray-700 mb-1"
                  >
                    {t("contactUs.message")}
                  </label>
                  <textarea
                    id="message"
                    {...getFieldProps("message")}
                    className={`w-full p-1 border rounded h-[100px] ${values.message && !values.message.length
                        ? "border-red-500"
                        : ""
                      }`}
                  />
                  <ErrorMessage
                    name="message"
                    component="div"
                    className="text-red-500 text-[12px]"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex col-span-1 md:col-span-2 lg:col-span-4 gap-4 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      handleReset();
                      setIsDropdownOpen(false);
                    }}
                    className="bg-transparent border border-red-500 text-red-500 px-4 py-1 rounded w-full text-[14px] sm:text-[16px]"
                  >
                    {t("contactUs.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-500 text-white px-4 py-1 rounded w-full text-[14px] sm:text-[16px]"
                  >
                    {t("contactUs.submit")}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      ) : otpPopup ? (
        <OTPScreen
          formValues={formValues}
          rncode={rncode}
          onClose={() => {
            setOtpPopup(false);
            setShowThankYou(true);
          }}
        />
      ) : (
        <ThankYouPopup onClose={handleThankYouClose} />
      )}
    </>
  );
};

export default ContactUs;
